import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';
import { timeFromFields } from '../../../utils/recipeMetadataReview';
import type { TimeValue } from '../../../types/recipeMetadata';

/**
 * The Time field's editor on a Review row: prep, cook and total in minutes,
 * pre-filled with what the chip shows. A blank total is prep + cook. Saving
 * is the reviewer's answer, like picking a tag, and writes the recipe's time
 * with `timeSource: 'person'`.
 */
interface Props {
  recipeId: string;
  initial: TimeValue | null;
  /** The line under the fields: where the pre-filled time came from. */
  hint?: string | null;
  saving: boolean;
  onSave: (time: TimeValue) => void;
}

const asText = (n: number | undefined): string => (n ? String(n) : '');

export const TimeEditor: React.FC<Props> = ({ recipeId, initial, hint, saving, onSave }) => {
  const { t } = useTranslation();
  const [prep, setPrep] = useState(asText(initial?.prep));
  const [cook, setCook] = useState(asText(initial?.cook));
  const [total, setTotal] = useState(asText(initial?.total));
  const [invalid, setInvalid] = useState(false);

  const save = () => {
    const time = timeFromFields(prep, cook, total);
    setInvalid(!time);
    if (time) onSave(time);
  };

  const field = (label: string, value: string, set: (v: string) => void, key: string, placeholder?: string) => (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        testID={`time-input-${key}-${recipeId}`}
        value={value}
        onChangeText={(v) => {
          set(v.replace(/[^0-9]/g, ''));
          setInvalid(false);
        }}
        keyboardType="number-pad"
        inputMode="numeric"
        placeholder={placeholder}
        placeholderTextColor={Paprika.textMuted}
        style={styles.input}
        onSubmitEditing={save}
      />
    </View>
  );

  return (
    <View style={styles.editor} testID={`time-editor-${recipeId}`}>
      <View style={styles.fields}>
        {field(t('recipeMetadata.timeHandsOn'), prep, setPrep, 'prep')}
        {field(t('recipeMetadata.timeCook'), cook, setCook, 'cook')}
        {field(t('recipeMetadata.timeReady'), total, setTotal, 'total', t('recipeMetadata.timeTotalHint'))}
        <TouchableOpacity
          testID={`time-save-${recipeId}`}
          style={styles.save}
          onPress={save}
          disabled={saving}
          activeOpacity={0.8}
        >
          {saving ? (
            <ActivityIndicator size="small" color={Paprika.onPrimary} />
          ) : (
            <Text style={styles.saveText}>{t('recipeMetadata.timeSave')}</Text>
          )}
        </TouchableOpacity>
      </View>
      {invalid ? <Text style={styles.invalid} testID={`time-invalid-${recipeId}`}>{t('recipeMetadata.timeInvalid')}</Text> : null}
      {hint ? <Text style={styles.hint}>{hint}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  editor: {
    gap: 4,
    paddingTop: 2,
  },
  fields: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: PaprikaSpacing.sm,
  },
  field: {
    gap: 2,
  },
  label: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
  },
  input: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    width: 132,
    minHeight: 34,
    paddingHorizontal: 10,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.surfaceLow,
  },
  save: {
    minHeight: 34,
    paddingHorizontal: 14,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.onPrimary,
  },
  invalid: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.approvalNo,
  },
  hint: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.ai,
  },
});

export default TimeEditor;
